import { useState } from "react";
import { motion, useScroll } from "framer-motion";
import { Flame, Menu, X } from "lucide-react";

const LINKS = [
  { href: "#our-story", label: "Our Story", testid: "nav-link-our-story" },
  { href: "#story", label: "Life Story", testid: "nav-link-story" },
  { href: "#teachings", label: "Teachings", testid: "nav-link-teachings" },
  { href: "#devotees", label: "Devotees", testid: "nav-link-devotees" },
  { href: "#gallery", label: "Gallery", testid: "nav-link-gallery" },
  { href: "#festivals", label: "Festivals", testid: "nav-link-festivals" },
  { href: "#meditations", label: "Meditations", testid: "nav-link-meditations" },
  { href: "#visit", label: "Visit", testid: "nav-link-visit" },
  { href: "/about-us", label: "About Us", testid: "nav-link-about-us" },
];

export default function Navbar({ basePath = "" }) {
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();

  return (
    <header data-testid="navbar" className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-amber-50/90 border-b border-amber-900/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href={basePath ? basePath : "#top"} data-testid="nav-logo" className="flex items-center gap-3 group">
            <span className="relative w-11 h-11 rounded-full overflow-hidden ring-2 ring-amber-600/50 shadow-md shadow-amber-900/25 group-hover:scale-105 group-hover:ring-amber-500 transition-all duration-300 shrink-0">
              <img src="/images/logo-baba.jpg" alt="Neem Karoli Baba" className="w-full h-full object-cover" />
            </span>
            <span className="leading-tight">
              <span className="block font-display text-sm sm:text-base font-semibold tracking-wide text-amber-950">
                Neem Karoli Baba Mandir
              </span>
              <span className="block text-[11px] uppercase tracking-[0.2em] text-amber-700/80">
                Osho Kaivalya Dham
              </span>
            </span>
          </a>

          <nav className="hidden xl:flex items-center gap-6" aria-label="Primary">
            {LINKS.map((l) => (
              <a key={l.href} href={l.href.startsWith("#") ? basePath + l.href : l.href} data-testid={l.testid}
                className="nav-link text-sm font-medium text-stone-700 hover:text-amber-800 transition-colors duration-300">
                {l.label}
              </a>
            ))}
            <a href="#donate" data-testid="nav-link-donate"
              className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-amber-600 to-orange-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-amber-900/25 hover:shadow-amber-900/40 hover:-translate-y-0.5 transition-all duration-300">
              <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent group-hover:translate-x-full transition-transform duration-700 ease-out" />
              <Flame className="h-4 w-4" /> Donate to the Mandir
            </a>
          </nav>

          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}
            className="xl:hidden p-2 rounded-md text-amber-950 hover:bg-amber-100 transition-colors"
            aria-label="Toggle menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <nav data-testid="nav-mobile-menu" className="xl:hidden bg-amber-50 border-t border-amber-900/10 px-4 py-4 space-y-1" aria-label="Mobile">
          {LINKS.map((l) => (
            <a key={l.href} href={l.href} data-testid={`${l.testid}-mobile`} onClick={() => setOpen(false)}
              className="block px-3 py-2.5 rounded-md text-sm font-medium text-stone-700 hover:bg-amber-100 hover:text-amber-900 transition-colors">
              {l.label}
            </a>
          ))}
          <a href="#donate" data-testid="nav-link-donate-mobile" onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold text-orange-700">
            <Flame className="h-4 w-4" /> Donate to the Mandir
          </a>
        </nav>
      )}

      <motion.div
        data-testid="scroll-progress"
        className="absolute bottom-0 left-0 right-0 h-[2px] origin-left bg-gradient-to-r from-amber-500 via-orange-600 to-red-600"
        style={{ scaleX: scrollYProgress }}
      />
    </header>
  );
}
